// @ts-nocheck
/**
 * <custom-register-form> — web component cho form đăng ký khách hàng.
 * Đã tối ưu Event Delegation, FormData và HTML5 Validation.
 */
(function () {
  if (window.customElements && customElements.get("custom-register-form"))
    return;

  const EMAIL_CONFIRM_DEBOUNCE_MS = 500;
  const TERMS_SCROLL_THRESHOLD = 0.95;
  const PHONE_REGEX =
    /^(0([1-9]{1}[1-9]\d{3}|[1-9]{2}\d{3}|[1-9]{2}\d{1}\d{2}|[1-9]{2}\d{2}\d{1})\d{4}|0[789]0\d{4}\d{4}|050\d{4}\d{4})$/;
  const DOB_MIN_AGE_YEARS = 120;

  const OCCUPATION_OPTIONS_INDIVIDUAL = [
    "会社員",
    "公務員",
    "自営業",
    "会社役員",
    "フリーランス",
    "専業主婦（夫）",
    "パート・アルバイト",
    "学生",
    "無職",
  ];
  const OCCUPATION_OPTIONS_CORPORATE = [
    "流通業",
    "建築・施工業",
    "リフォーム店",
    "設計事務所",
    "店舗設計",
    "その他",
  ];

  const MESSAGES = {
    required: "必須項目を入力してください。",
    invalidPhone: "電話番号の形式が正しくありません。",
    checkInput: "入力内容をご確認ください。",
    submitError: "登録に失敗しました。入力内容をご確認ください。",
    networkError: "通信エラーが発生しました。時間をおいて再度お試しください。",
    submitting: "送信中...",
    emailTaken: "このメールアドレスは既に登録されています。",
    phoneTaken: "この電話番号は既に登録されています。",
    emailConfirmRequired: "メールアドレス（確認）を入力してください。",
    emailConfirmMismatch: "メールアドレスが一致しません。",
    termsRequired: "利用規約とプライバシーポリシーに同意してください。",
    dobIncomplete: "生年月日は年・月・日すべて入力してください。",
  };

  const SERVER_ERROR_TRANSLATIONS = {
    "email has already been taken": MESSAGES.emailTaken,
    "phone has already been taken": MESSAGES.phoneTaken,
  };

  function translateFieldError(fieldName, message) {
    if (!message) return message;
    const normalized = String(message).trim().toLowerCase();
    if (SERVER_ERROR_TRANSLATIONS[normalized])
      return SERVER_ERROR_TRANSLATIONS[normalized];
    if (normalized.includes("already been taken")) {
      if (fieldName === "email" || normalized.includes("email"))
        return MESSAGES.emailTaken;
      if (fieldName === "phone_number" || normalized.includes("phone"))
        return MESSAGES.phoneTaken;
    }
    return String(message).trim();
  }

  function normalizePhone(raw) {
    const cleaned = String(raw || "").replace(/[\s\-()]/g, "");
    if (cleaned.startsWith("+")) return cleaned;
    if (cleaned.startsWith("0")) return "+81" + cleaned.slice(1);
    return "+81" + cleaned;
  }

  class CustomRegisterForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector("form");
      if (!this.form || this.dataset.init === "1") return;
      this.dataset.init = "1";

      // Chỉ lưu tham chiếu các phần tử KHÔNG PHẢI form field thông thường
      this.proxyPath = this.getAttribute("proxy-path") || "/apps/proxy";
      this.actionValue =
        this.getAttribute("action-value") || "customer_register";
      this.successMessage =
        this.getAttribute("success-message") || "登録が完了しました。";
      this.redirectUrl = this.getAttribute("redirect-url") || "";

      this.alertBox = this.querySelector("[data-crf-alert]");
      this.submitBtn = this.querySelector("[data-crf-submit]");
      this.termsScroll = this.querySelector("[data-crf-terms-scroll]");
      this.termsCheckbox = this.querySelector("[data-crf-terms-agree]");
      this.termsAgreeLabel = this.querySelector("[data-crf-terms-agree-label]");

      this._isSubmitting = false;
      this._lastLookedUpZip = "";
      this._emailConfirmTimer = null;
      this._termsScrolledEnough = false;

      this.bindEvents();
      this.initDobPicker();
      this.initTermsAgreement();

      // Chạy check entity (Pháp nhân) lần đầu
      this.toggleCompanyWrap(this.field("entity_type")?.value);
    }

    disconnectedCallback() {
      this.form?.removeEventListener("submit", this.handlers.onSubmit);
      this.form?.removeEventListener("input", this.handlers.onInput); // Event Delegation
      this.form?.removeEventListener("focusout", this.handlers.onFocusOut);
      this.termsScroll?.removeEventListener(
        "scroll",
        this.handlers.onTermsScroll,
      );
      this.termsCheckbox?.removeEventListener(
        "change",
        this.handlers.onTermsAgreeChange,
      );
      if (this._emailConfirmTimer) clearTimeout(this._emailConfirmTimer);
    }

    bindEvents() {
      this.handlers = {
        onSubmit: this.onSubmit.bind(this),
        onInput: this.handleFormInput.bind(this), // Hứng toàn bộ sự kiện gõ phím
        onFocusOut: this.handleFormFocusOut.bind(this), // Hứng toàn bộ sự kiện blur
        onTermsScroll: this.checkTermsScrollProgress.bind(this),
        onTermsAgreeChange: this.updateSubmitState.bind(this),
      };

      this.form.addEventListener("submit", this.handlers.onSubmit);
      this.form.addEventListener("input", this.handlers.onInput);
      this.form.addEventListener("focusout", this.handlers.onFocusOut);
    }

    // Tiện ích lấy field theo name
    field(name) {
      return this.form.querySelector(`[name="${name}"]`);
    }

    // --- EVENT DELEGATION (Xử lý tập trung mọi input) ---
    handleFormInput(e) {
      const el = e.target;
      if (!el.name && !el.hasAttribute("data-crf-email-confirm")) return;

      // 1. Xóa báo lỗi khi người dùng bắt đầu sửa
      if (el.getAttribute("aria-invalid") === "true") this.clearFieldError(el);

      // 2. Định tuyến logic theo tên field
      switch (el.name) {
        case "entity_type":
          this.toggleCompanyWrap(el.value);
          break;
        case "postal_code":
          this.handleZipInput(el);
          break;
        case "email":
          this.scheduleEmailConfirmValidation();
          break;
      }

      // Xử lý riêng cho ô xác nhận email (vì nó không có thuộc tính name)
      if (el.hasAttribute("data-crf-email-confirm")) {
        this.scheduleEmailConfirmValidation();
      }
    }

    handleFormFocusOut(e) {
      const el = e.target;
      if (el.name === "postal_code") this.lookupZip(el.value);
      if (el.hasAttribute("data-crf-email-confirm"))
        this.validateEmailConfirm();
    }

    // --- LOGIC CHO TỪNG THÀNH PHẦN ---
    toggleCompanyWrap(entityValue) {
      const wrap = this.querySelector("[data-crf-company]");
      const input = this.field("company_name");
      const reqMark = this.querySelector("[data-crf-company-req]");
      const isCorp = entityValue === "法人";

      if (wrap) wrap.hidden = !isCorp;
      if (input) {
        if (isCorp) {
          input.setAttribute("required", "");
        } else {
          input.removeAttribute("required");
          this.clearFieldError(input);
        }
      }
      if (reqMark) reqMark.hidden = !isCorp;
      this.updateOccupationOptions(isCorp);
    }

    updateOccupationOptions(isCorp) {
      const select = this.field("occupation");
      if (!select) return;

      const options = isCorp
        ? OCCUPATION_OPTIONS_CORPORATE
        : OCCUPATION_OPTIONS_INDIVIDUAL;
      const prev = select.value;

      select.innerHTML = '<option value="">選択してください</option>';
      for (const label of options) {
        select.insertAdjacentHTML(
          "beforeend",
          `<option value="${label}">${label}</option>`,
        );
      }

      if (prev && options.includes(prev)) {
        select.value = prev;
      } else {
        select.value = "";
        if (prev) this.clearFieldError(select);
      }
    }

    handleZipInput(inputEl) {
      let cleaned = inputEl.value.replace(/[^0-9]/g, "").substring(0, 7);
      inputEl.value =
        cleaned.length > 3
          ? cleaned.substring(0, 3) + "-" + cleaned.substring(3)
          : cleaned;
      if (cleaned.length === 7) this.lookupZip(cleaned);
    }

    lookupZip(zipRaw) {
      const zip = zipRaw.replace(/[^0-9]/g, "");
      if (zip.length !== 7 || zip === this._lastLookedUpZip) return;
      this._lastLookedUpZip = zip;

      fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${zip}`)
        .then((r) => r.json())
        .then((data) => {
          const res = data?.results?.[0];
          if (!res) return (this._lastLookedUpZip = "");
          const pref = this.field("prefecture");
          const city = this.field("city");
          if (res.address1 && pref) pref.value = res.address1;
          if ((res.address2 || res.address3) && city)
            city.value = (res.address2 || "") + (res.address3 || "");
        })
        .catch(() => {
          this._lastLookedUpZip = "";
        });
    }

    scheduleEmailConfirmValidation() {
      if (this._emailConfirmTimer) clearTimeout(this._emailConfirmTimer);
      this._emailConfirmTimer = setTimeout(
        () => this.validateEmailConfirm(),
        EMAIL_CONFIRM_DEBOUNCE_MS,
      );
    }

    validateEmailConfirm() {
      const confirmEl = this.querySelector("[data-crf-email-confirm]");
      const emailEl = this.field("email");
      if (!confirmEl || !emailEl) return true;

      const confirmVal = confirmEl.value.trim();
      const emailVal = emailEl.value.trim();

      if (!confirmVal) {
        this.setInputError(confirmEl, MESSAGES.emailConfirmRequired);
        return false;
      }
      if (confirmVal !== emailVal) {
        this.setInputError(confirmEl, MESSAGES.emailConfirmMismatch);
        return false;
      }
      this.clearFieldError(confirmEl);
      return true;
    }

    // --- UI Helpers ---
    showAlert(message, type) {
      if (!this.alertBox) return;
      this.alertBox.textContent = message;
      this.alertBox.className = `crf__alert crf__alert--${type}`;
      this.alertBox.hidden = false;
      this.alertBox.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }

    clearAlert() {
      if (this.alertBox) {
        this.alertBox.hidden = true;
        this.alertBox.textContent = "";
      }
    }

    setFieldError(nameOrEl, message) {
      const el = typeof nameOrEl === "string" ? this.field(nameOrEl) : nameOrEl;
      this.setInputError(
        el,
        message,
        typeof nameOrEl === "string" ? nameOrEl : el.name,
      );
    }

    setInputError(el, message, fieldName) {
      if (!el) return;
      el.setAttribute("aria-invalid", "true");
      el.classList.add("crf__input--invalid");
      this._syncDobTriggerInvalidState(el, true);

      const wrap = el.closest(".crf__field") || el.parentElement;
      let msgEl = wrap.querySelector("[data-crf-field-error]");
      if (!msgEl) {
        msgEl = document.createElement("p");
        msgEl.className = "crf__field-error";
        msgEl.setAttribute("data-crf-field-error", "");
        wrap.appendChild(msgEl);
      }
      msgEl.textContent = translateFieldError(fieldName || "", message);
    }

    clearFieldError(el) {
      if (!el) return;
      el.removeAttribute("aria-invalid");
      el.classList.remove("crf__input--invalid");
      this._syncDobTriggerInvalidState(el, false);
      el.closest(".crf__field")
        ?.querySelector("[data-crf-field-error]")
        ?.remove();
    }

    clearAllFieldErrors() {
      this.form
        .querySelectorAll('[aria-invalid="true"]')
        .forEach((el) => this.clearFieldError(el));
    }

    // --- Validate Tổng hợp (Động) ---
    validate() {
      this.clearAllFieldErrors();
      let firstInvalid = null;

      // 1. Quét tự động HTML5 required attributes
      const elements = Array.from(this.form.elements);
      for (const el of elements) {
        // Bỏ qua field công ty nếu đang ẩn
        if (
          el.name === "company_name" &&
          this.querySelector("[data-crf-company]")?.hidden
        )
          continue;

        if (el.hasAttribute("required") && !el.value.trim()) {
          this.setFieldError(el, MESSAGES.required);
          if (!firstInvalid) firstInvalid = el;
        }
      }

      // 2. Validate Phone Format (Nếu có nhập)
      const phoneEl = this.field("phone_number");
      if (
        phoneEl?.value.trim() &&
        !PHONE_REGEX.test(phoneEl.value.replace(/[\s\-()]/g, ""))
      ) {
        this.setFieldError(phoneEl, MESSAGES.invalidPhone);
        if (!firstInvalid) firstInvalid = phoneEl;
      }

      // 3. Email Confirm & DOB
      if (!this.validateEmailConfirm() && !firstInvalid)
        firstInvalid = this.querySelector("[data-crf-email-confirm]");
      if (!this.validateDobPartial() && !firstInvalid) {
        firstInvalid = !this.dobParts.year.value
          ? this.dobParts.year
          : !this.dobParts.month.value
            ? this.dobParts.month
            : this.dobParts.day;
      }

      // 4. Terms Checked
      if (
        !firstInvalid &&
        (!this.termsCheckbox || !this.termsCheckbox.checked)
      ) {
        this.termsScroll?.focus();
        return MESSAGES.termsRequired;
      }

      if (firstInvalid) {
        firstInvalid.focus();
        return MESSAGES.checkInput;
      }
      return null;
    }

    // --- Collect Data (Động) ---
    collectPayload() {
      // Dùng FormData để tự động gom mọi giá trị
      const formData = new FormData(this.form);
      const data = { action: this.actionValue };
      const isCompanyHidden = this.querySelector("[data-crf-company]")?.hidden;

      for (let [name, value] of formData.entries()) {
        value = value.toString().trim();
        if (!value) continue; // Bỏ qua field trống
        if (isCompanyHidden && name === "company_name") continue; // Bỏ qua tên công ty nếu chọn Cá nhân

        // Transform data
        if (name === "phone_number") value = normalizePhone(value);
        if (name === "postal_code") value = value.replace(/[^0-9]/g, "");

        data[name] = value;
      }
      return data;
    }

    async onSubmit(e) {
      e.preventDefault();
      this.clearAlert();

      const message = this.validate();
      if (message) return this.showAlert(message, "error");

      this.setLoading(true);
      try {
        const res = await fetch(this.proxyPath, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(this.collectPayload()),
        });

        const json = await res.json().catch(() => ({}));
        const fieldErrors = json?.details?.fieldErrors;

        if (
          !res.ok ||
          json.success === false ||
          json.error ||
          (fieldErrors && Object.keys(fieldErrors).length)
        ) {
          if (fieldErrors) {
            let firstErr = null;
            Object.entries(fieldErrors).forEach(([name, err]) => {
              this.setFieldError(name, err);
              if (!firstErr) firstErr = this.field(name);
            });
            firstErr?.focus();
            this.showAlert(MESSAGES.checkInput, "error");
          } else {
            this.showAlert(
              json.error || json.message || MESSAGES.submitError,
              "error",
            );
          }
          return;
        }

        this.showAlert(this.successMessage, "success");
        this.form.reset();
        this.clearAllFieldErrors();
        this.toggleCompanyWrap(this.field("entity_type")?.value);
        this.syncDobPickerState();
        this.resetTermsAgreement();
        if (this.redirectUrl) window.location.href = this.redirectUrl;
      } catch (err) {
        this.showAlert(MESSAGES.networkError, "error");
      } finally {
        this.setLoading(false);
      }
    }

    // --- Các hàm DOB & Terms Agreement giữ nguyên vì logic phụ thuộc nhiều vào UI tùy biến ---
    // (Đã gộp ở bước tối ưu trước đó để giữ code ngắn gọn)

    initDobPicker() {
      this.dobWrap = this.querySelector("[data-crf-dob]");
      if (!this.dobWrap || this.dobWrap.dataset.bound === "1") return;
      this.dobWrap.dataset.bound = "1";
      this.dobParts = {
        value: this.dobWrap.querySelector("[data-crf-dob-value]"),
        year: this.dobWrap.querySelector("[data-crf-dob-year]"),
        yearList: this.dobWrap.querySelector("[data-crf-dob-year-list]"),
        month: this.dobWrap.querySelector("[data-crf-dob-month]"),
        monthList: this.dobWrap.querySelector("[data-crf-dob-month-list]"),
        day: this.dobWrap.querySelector("[data-crf-dob-day]"),
        dayList: this.dobWrap.querySelector("[data-crf-dob-day-list]"),
      };
      this.populateDobOptions();
      ["year", "month", "day"].forEach((part) => this.initDobPart(part));
      this.syncDobPickerState();
    }
    initDobPart(type) {
      const select = this.dobParts[type],
        list = this.dobParts[`${type}List`];
      if (!select || !list) return;
      select.addEventListener("change", () => this.onDobSelectChange(type));
      select.addEventListener("mousedown", (e) => {
        e.preventDefault();
        list.hidden ? this.openDobList(type) : this.closeAllDobLists();
      });
      list.addEventListener("change", () => {
        select.value = list.value;
        this.closeAllDobLists();
        this.onDobSelectChange(type);
      });
      list.addEventListener("blur", () =>
        setTimeout(() => this.closeAllDobLists(), 150),
      );
    }
    openDobList(type) {
      this.closeAllDobLists();
      const select = this.dobParts[type],
        list = this.dobParts[`${type}List`];
      if (type === "month" && list.options.length > 0)
        list.size = list.options.length;
      list.innerHTML = select.innerHTML;
      list.value = select.value;
      list.hidden = false;
      list.focus();
      this._activeOutsideClick = (e) => {
        if (select.closest(`.crf__dob-${type}-wrap`)?.contains(e.target))
          return;
        this.closeAllDobLists();
      };
      requestAnimationFrame(() =>
        document.addEventListener(
          "pointerdown",
          this._activeOutsideClick,
          true,
        ),
      );
    }
    closeAllDobLists() {
      ["year", "month", "day"].forEach((type) => {
        if (this.dobParts[`${type}List`])
          this.dobParts[`${type}List`].hidden = true;
      });
      if (this._activeOutsideClick) {
        document.removeEventListener(
          "pointerdown",
          this._activeOutsideClick,
          true,
        );
        this._activeOutsideClick = null;
      }
    }
    onDobSelectChange(changedType) {
      if (changedType !== "day") this.populateDobDayOptions();
      this.syncHiddenDobFromSelects();
    }
    populateDobOptions() {
      if (this.dobParts.year) {
        const cy = new Date().getFullYear();
        this.dobParts.year.innerHTML = '<option value=""></option>';
        for (let y = cy; y >= cy - DOB_MIN_AGE_YEARS; y--)
          this.dobParts.year.insertAdjacentHTML(
            "beforeend",
            `<option value="${y}">${y}</option>`,
          );
      }
      if (this.dobParts.month) {
        this.dobParts.month.innerHTML = '<option value=""></option>';
        for (let m = 1; m <= 12; m++)
          this.dobParts.month.insertAdjacentHTML(
            "beforeend",
            `<option value="${String(m).padStart(2, "0")}">${String(m).padStart(2, "0")}</option>`,
          );
      }
      this.populateDobDayOptions();
    }
    populateDobDayOptions() {
      if (!this.dobParts.day) return;
      const y = parseInt(this.dobParts.year?.value || "0", 10),
        m = parseInt(this.dobParts.month?.value || "0", 10);
      const maxDay = y && m ? new Date(y, m, 0).getDate() : 31;
      const prev = this.dobParts.day.value;
      this.dobParts.day.innerHTML = '<option value=""></option>';
      for (let d = 1; d <= maxDay; d++)
        this.dobParts.day.insertAdjacentHTML(
          "beforeend",
          `<option value="${String(d).padStart(2, "0")}">${String(d).padStart(2, "0")}</option>`,
        );
      if (prev && this.dobParts.day.querySelector(`option[value="${prev}"]`))
        this.dobParts.day.value = prev;
    }
    syncHiddenDobFromSelects() {
      if (!this.dobParts.value) return;
      const y = this.dobParts.year?.value,
        m = this.dobParts.month?.value,
        d = this.dobParts.day?.value;
      this.dobParts.value.value = y && m && d ? `${y}-${m}-${d}` : "";
      this.dobParts.value.dispatchEvent(new Event("input", { bubbles: true }));
    }
    syncDobPickerState() {
      const val = this.dobParts.value?.value;
      if (val && val.split("-").length === 3) {
        const [y, m, d] = val.split("-");
        if (this.dobParts.year) this.dobParts.year.value = y;
        if (this.dobParts.month) this.dobParts.month.value = m;
        this.populateDobDayOptions();
        if (this.dobParts.day) this.dobParts.day.value = d;
      }
    }
    _syncDobTriggerInvalidState(el, isInvalid) {
      if (!el || !el.hasAttribute("data-crf-dob-value")) return;
      el.closest("[data-crf-dob]")
        ?.querySelectorAll(
          "[data-crf-dob-year], [data-crf-dob-month], [data-crf-dob-day]",
        )
        .forEach((s) => {
          isInvalid
            ? s.classList.add("crf__input--invalid")
            : s.classList.remove("crf__input--invalid");
          isInvalid
            ? s.setAttribute("aria-invalid", "true")
            : s.removeAttribute("aria-invalid");
        });
    }
    validateDobPartial() {
      if (!this.dobParts.value) return true;
      const count = [
        this.dobParts.year?.value,
        this.dobParts.month?.value,
        this.dobParts.day?.value,
      ].filter(Boolean).length;
      if (count === 0 || count === 3) {
        this.clearFieldError(this.dobParts.value);
        return true;
      }
      this.setFieldError("date_of_birth", MESSAGES.dobIncomplete);
      return false;
    }

    initTermsAgreement() {
      if (!this.termsScroll || !this.termsCheckbox) return;
      this.resetTermsAgreement();
      this.termsScroll.addEventListener("scroll", this.handlers.onTermsScroll);
      this.termsCheckbox.addEventListener(
        "change",
        this.handlers.onTermsAgreeChange,
      );
    }
    resetTermsAgreement() {
      this._termsScrolledEnough = false;
      if (this.termsScroll) this.termsScroll.scrollTop = 0;
      if (this.termsCheckbox) {
        this.termsCheckbox.checked = false;
        this.termsCheckbox.disabled = true;
      }
      this.updateTermsCheckboxUi();
      this.updateSubmitState();
      requestAnimationFrame(() => this.checkTermsScrollProgress());
    }
    checkTermsScrollProgress() {
      if (!this.termsScroll || this._termsScrolledEnough) return;
      const scrollable =
        this.termsScroll.scrollHeight - this.termsScroll.clientHeight;
      if (
        scrollable <= 0 ||
        this.termsScroll.scrollTop / scrollable >= TERMS_SCROLL_THRESHOLD
      ) {
        this._termsScrolledEnough = true;
        if (this.termsCheckbox) this.termsCheckbox.disabled = false;
        this.updateTermsCheckboxUi();
      }
    }
    updateTermsCheckboxUi() {
      this.termsAgreeLabel?.classList.toggle(
        "crf__terms-agree--disabled",
        !!this.termsCheckbox?.disabled,
      );
    }
    updateSubmitState() {
      if (this.submitBtn)
        this.submitBtn.disabled =
          this._isSubmitting || !this.termsCheckbox?.checked;
    }
    setLoading(isLoading) {
      if (!this.submitBtn) return;
      this._isSubmitting = isLoading;
      if (isLoading) {
        this.submitBtn.disabled = true;
        this._originalBtnLabel = this.submitBtn.textContent;
        this.submitBtn.textContent = MESSAGES.submitting;
      } else {
        if (this._originalBtnLabel)
          this.submitBtn.textContent = this._originalBtnLabel;
        this.updateSubmitState();
      }
    }
  }

  customElements.define("custom-register-form", CustomRegisterForm);
})();
